import './instrument'

import App from './App'
import { createRoot } from 'react-dom/client'
import { initAmplitude } from 'lib/amplitude'
import { AuthProvider } from 'components/auth'
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { ErrorBoundary, TelemetryProvider } from '@workwhile/telemetry/react'
import { getTelemetryClient } from '@workwhile/telemetry'
import { TopLevelErrorBoundary } from 'components/ErrorBoundary'
import { config } from 'config'

initAmplitude()

const logQueryClientError = (label: string) => (error) => {
  if (config.env !== 'development') {
    return
  }
  // eslint-disable-next-line no-console
  console.error(`${label} error:`, error)
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: logQueryClientError('Query'),
  }),
  mutationCache: new MutationCache({
    onError: logQueryClientError('Mutation'),
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // dont retry on these statuses
        if (['403', '404'].includes((error as Error).name)) {
          return false
        }

        return failureCount < 3
      },
    },
  },
})

const container = document.getElementById('root')

if (container) {
  const root = createRoot(container, {
    onUncaughtError: getTelemetryClient().onUncaughtError(
      (error, errorInfo) => {
        if (config.env === 'development') {
          // eslint-disable-next-line no-console
          console.warn('Uncaught error', error, errorInfo.componentStack)
        }
      }
    ),

    onCaughtError: getTelemetryClient().onCaughtError(),
    onRecoverableError: getTelemetryClient().onRecoverableError(),
  })

  root.render(
    <ErrorBoundary FallbackComponent={TopLevelErrorBoundary}>
      <TelemetryProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <App />
          </AuthProvider>
        </QueryClientProvider>
      </TelemetryProvider>
    </ErrorBoundary>
  )
}
